import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// Services grouped by profession for dropdown display
const SERVICES_BY_PROFESSION = [
  {
    profession: 'Dentists',
    slug: 'dentist',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2C9.5 2 7 3.5 7 6c0 1.5.5 3 .5 4.5 0 2-.5 4.5-.5 6.5 0 2 .5 3 1.5 3s1.5-1 2-3c.5 2 1 3 2 3s1.5-1 1.5-3c0-2-.5-4.5-.5-6.5C13.5 9 14 7.5 14 6c0-2.5-2.5-4-2-4Z"/>
      </svg>
    ),
    services: [
      { name: 'AI Front Desk', slug: 'ai-front-desk' },
      { name: 'Appointment Scheduling', slug: 'appointment-scheduling' },
      { name: 'Patient Intake', slug: 'patient-intake' },
      { name: 'Local SEO', slug: 'seo' },
      { name: 'Missed Call Recovery', slug: 'missed-call-recovery' },
    ],
  },
  {
    profession: 'Lawyers',
    slug: 'lawyer',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 3v4a1 1 0 0 0 1 1h4"/>
        <path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z"/>
        <path d="M9 17h6"/>
        <path d="M9 13h6"/>
      </svg>
    ),
    services: [
      { name: 'AI Answering Service', slug: 'ai-front-desk' },
      { name: 'Lead Intake', slug: 'lead-intake' },
      { name: 'Case Qualification', slug: 'case-qualification' },
      { name: 'Consultation Booking', slug: 'appointment-scheduling' },
      { name: 'Local SEO', slug: 'seo' },
    ],
  },
  {
    profession: 'Accountants',
    slug: 'accountant',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 12V7H5a2 2 0 0 1 0-4h14v4"/>
        <path d="M3 5v14a2 2 0 0 0 2 2h16v-5"/>
        <path d="M18 12a2 2 0 0 0 0 4h4v-4Z"/>
      </svg>
    ),
    services: [
      { name: 'AI Receptionist', slug: 'ai-front-desk' },
      { name: 'Client Onboarding', slug: 'client-onboarding' },
      { name: 'Secure Documents', slug: 'secure-documents' },
      { name: 'Appointment Reminders', slug: 'appointment-reminders' },
      { name: 'SEO', slug: 'seo' },
    ],
  },
  {
    profession: 'Real Estate',
    slug: 'real-estate',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
        <path d="M9 22V12h6v10"/>
      </svg>
    ),
    services: [
      { name: 'AI Call Answering', slug: 'ai-call-answering' },
      { name: 'Lead Qualification', slug: 'lead-qualification' },
      { name: 'Showing Scheduler', slug: 'appointment-scheduling' },
      { name: 'Google Business', slug: 'seo' },
      { name: 'Automated Follow-ups', slug: 'automated-followups' },
    ],
  },
  {
    profession: 'Medical Clinics',
    slug: 'medical-clinic',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M19 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2z"/>
        <path d="M12 8v8"/>
        <path d="M8 12h8"/>
      </svg>
    ),
    services: [
      { name: 'HIPAA AI Front Desk', slug: 'ai-front-desk' },
      { name: 'Appointment Scheduling', slug: 'appointment-scheduling' },
      { name: 'Patient Reminders', slug: 'appointment-reminders' },
      { name: 'Missed Call Recovery', slug: 'missed-call-recovery' },
      { name: 'Maps SEO', slug: 'seo' },
    ],
  },
  {
    profession: 'Small Business',
    slug: 'small-business',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
        <path d="m3.27 6.96 8.73 5.04 8.73-5.04M12 22.08V12"/>
      </svg>
    ),
    services: [
      { name: 'AI Front Desk', slug: 'ai-front-desk' },
      { name: 'Call Routing', slug: 'call-routing' },
      { name: 'Booking & Calendar', slug: 'calendar-sync' },
      { name: 'Website SEO', slug: 'seo' },
      { name: 'Lead Analytics', slug: 'analytics' },
    ],
  },
];

export default function ServicesDropdown() {
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [selectedIndustry, setSelectedIndustry] = useState(SERVICES_BY_PROFESSION[0]);
  const dropdownRef = useRef(null);
  const timeoutRef = useRef(undefined);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 992);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleMouseEnter = useCallback(() => {
    if (isMobile) return;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsOpen(true);
  }, [isMobile]);

  const handleMouseLeave = useCallback(() => {
    if (isMobile) return;
    timeoutRef.current = setTimeout(() => setIsOpen(false), 150);
  }, [isMobile]);

  const handleClick = useCallback(() => {
    if (isMobile) setIsOpen((prev) => !prev);
  }, [isMobile]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) setIsOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  return (
    <div
      className="services-dropdown-wrapper"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      ref={dropdownRef}
      role="navigation"
      aria-label="Services menu"
    >
      <button
        className="nav-link fw-medium services-dropdown-trigger"
        onClick={handleClick}
        aria-expanded={isOpen}
        aria-haspopup="true"
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '0.375rem',
          padding: '0.5rem 0.75rem',
        }}
      >
        Services
        <svg
          width="10"
          height="10"
          viewBox="0 0 10 10"
          fill="none"
          style={{
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          <path
            d="M2 3.5L5 6.5L8 3.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="services-mega-menu"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
            role="menu"
            aria-label="Services categories"
            style={{
              position: 'fixed',
              top: '80px',
              left: 0,
              right: 0,
              width: '100vw',
              zIndex: 1090,
            }}
          >
            <div
              style={{
                background: '#ffffff',
                borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              {/* Radial gradient blur */}
              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '800px',
                  height: '600px',
                  background: 'radial-gradient(ellipse, rgba(99, 102, 241, 0.12) 0%, rgba(139, 92, 246, 0.08) 30%, rgba(99, 102, 241, 0.04) 50%, transparent 70%)',
                  filter: 'blur(80px)',
                  pointerEvents: 'none',
                  zIndex: 0,
                }}
              />

              {/* Two Column Layout */}
              <div
                style={{
                  position: 'relative',
                  display: 'flex',
                  maxWidth: '1400px',
                  margin: '0 auto',
                  padding: '2rem 3rem',
                  gap: '2rem',
                  zIndex: 1,
                }}
              >
                {/* Left Column - Industries */}
                <div
                  style={{
                    flex: '0 0 14%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.5rem',
                  }}
                >
                  {SERVICES_BY_PROFESSION.map((profession) => (
                    <button
                      key={profession.slug}
                      onClick={() => setSelectedIndustry(profession)}
                      onMouseEnter={() => setSelectedIndustry(profession)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem',
                        padding: '0.875rem 1rem',
                        background: selectedIndustry.slug === profession.slug
                          ? 'rgba(99, 102, 241, 0.08)'
                          : 'transparent',
                        border: 'none',
                        borderRadius: '10px',
                        color: selectedIndustry.slug === profession.slug
                          ? '#4f46e5'
                          : '#6b7280',
                        fontSize: '0.875rem',
                        fontWeight: selectedIndustry.slug === profession.slug ? 600 : 500,
                        cursor: 'pointer',
                        textAlign: 'left',
                        transition: 'all 0.2s ease',
                      }}
                      onMouseOver={(e) => {
                        if (selectedIndustry.slug !== profession.slug) {
                          e.currentTarget.style.background = 'rgba(0, 0, 0, 0.03)';
                          e.currentTarget.style.color = '#374151';
                        }
                      }}
                      onMouseOut={(e) => {
                        if (selectedIndustry.slug !== profession.slug) {
                          e.currentTarget.style.background = 'transparent';
                          e.currentTarget.style.color = '#6b7280';
                        }
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: selectedIndustry.slug === profession.slug
                            ? '#6366f1'
                            : '#9ca3af',
                          transition: 'all 0.2s ease',
                        }}
                      >
                        {profession.icon}
                      </div>
                      <span>{profession.profession}</span>
                    </button>
                  ))}
                </div>

                {/* Right Column - Services */}
                <div
                  style={{
                    flex: '1',
                    padding: '1rem 1.5rem',
                    borderRadius: '14px',
                    background: 'linear-gradient(90deg, rgba(205, 239, 251, 0.3) 0%, rgba(255, 255, 255, 0.5) 33.23%, rgba(255, 255, 255, 0.5) 65.77%, rgba(253, 238, 203, 0.3) 100%)',
                    border: '1px solid rgba(0, 0, 0, 0.06)',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={selectedIndustry.slug}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.2 }}
                      style={{ position: 'relative', zIndex: 1 }}
                    >
                      {/* Header */}
                      <div style={{ marginBottom: '1.5rem' }}>
                        <h3
                          style={{
                            margin: '0 0 0.5rem 0',
                            fontSize: '1.25rem',
                            fontWeight: 600,
                            color: '#111827',
                            letterSpacing: '-0.02em',
                          }}
                        >
                          {selectedIndustry.profession} Services
                        </h3>
                        <a
                          href={`https://www.reasoningcompany.com/services#${selectedIndustry.slug}`}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.25rem',
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            color: '#6366f1',
                            textDecoration: 'none',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            transition: 'color 0.2s ease',
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.color = '#4f46e5')}
                          onMouseLeave={(e) => (e.currentTarget.style.color = '#6366f1')}
                        >
                          View industry overview
                          <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M2 5h6M5.5 2.5l2.5 2.5-2.5 2.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </a>
                      </div>

                      {/* Services Grid */}
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                          gap: '1rem',
                        }}
                      >
                        {selectedIndustry.services.map((service) => (
                          <a
                            key={service.slug + service.name}
                            href={`https://www.reasoningcompany.com/services/${service.slug}`}
                            style={{
                              display: 'block',
                              padding: '1rem 1.25rem',
                              borderRadius: '10px',
                              background: '#ffffff',
                              border: '1px solid rgba(0, 0, 0, 0.08)',
                              color: '#374151',
                              textDecoration: 'none',
                              fontSize: '0.9375rem',
                              fontWeight: 500,
                              transition: 'all 0.2s ease',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = 'rgba(99, 102, 241, 0.05)';
                              e.currentTarget.style.borderColor = 'rgba(99, 102, 241, 0.2)';
                              e.currentTarget.style.transform = 'translateX(4px)';
                              e.currentTarget.style.color = '#4f46e5';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = '#ffffff';
                              e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.08)';
                              e.currentTarget.style.transform = 'translateX(0)';
                              e.currentTarget.style.color = '#374151';
                            }}
                          >
                            {service.name}
                          </a>
                        ))}
                      </div>
                    </motion.div>
                  </AnimatePresence>
                </div>
              </div>

              {/* Bottom CTA Bar */}
              <div
                style={{
                  borderTop: '1px solid rgba(0, 0, 0, 0.08)',
                  background: 'rgba(249, 250, 251, 0.8)',
                  position: 'relative',
                  zIndex: 1,
                }}
              >
                <div
                  style={{
                    padding: '1rem 3rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '1.5rem',
                    flexWrap: 'wrap',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', flexWrap: 'wrap' }}>
                    <p
                      style={{
                        margin: 0,
                        fontSize: '0.875rem',
                        color: '#6b7280',
                      }}
                    >
                      Don't see what you need? We build custom AI & automation solutions.
                    </p>
                    <a
                      href="https://www.reasoningcompany.com/services/request"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.375rem',
                        fontSize: '0.8125rem',
                        color: '#6366f1',
                        textDecoration: 'none',
                        transition: 'color 0.2s ease',
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.color = '#4f46e5')}
                      onMouseLeave={(e) => (e.currentTarget.style.color = '#6366f1')}
                    >
                      Request custom solution
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M2.5 6h7M6.5 3l3 3-3 3" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </a>
                  </div>
                  <a
                    href="https://www.reasoningcompany.com/services"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.625rem 1.25rem',
                      background: '#111827',
                      color: '#ffffff',
                      borderRadius: '8px',
                      fontSize: '0.8125rem',
                      fontWeight: 600,
                      textDecoration: 'none',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                      whiteSpace: 'nowrap',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-1px)';
                      e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.25)';
                      e.currentTarget.style.background = '#000000';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.15)';
                      e.currentTarget.style.background = '#111827';
                    }}
                  >
                    View All Services
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M2.5 6h7M6.5 3l3 3-3 3" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
